import { axios, AxiosInstance, toArray } from "@ksxz/axios";

import {
  AxiosCaseConverter,
  AxiosExtraHeaders,
  AxiosJsonBigint,
  AxiosNormalizeError,
  AxiosProgressPercent
} from "@ksxz/axios-plugins";

import axiosRetry from "axios-retry-x";
import createAuthRefresh from "axios-auth-refresh";

import { useGatewayAxiosPlugin } from "@ksxz/gateway-plugin-axios";

import { GatewayHttpHandler } from "@ksxz/gateway";
import defaultsDeep from "lodash/defaultsDeep";
import { ApiClientAxiosRefreshOptions, ApiClientOptions } from "./types";
import { createDefaultsExtraHeaders } from "./headers";

export const apiClientDefaultsOptions: ApiClientOptions = {
  service: false,
  csrftoken: false,
  cid: false,
  origin: "",
  config: {
    withCredentials: true
  }
};

export const mergeApiClientOptions = (options?: Partial<ApiClientOptions>) => {
  const result: ApiClientOptions = defaultsDeep(
    options,
    apiClientDefaultsOptions
  );
  return result;
};

export function useApiClientAuthRefresh(
  axios: AxiosInstance,
  refreshAuth: ApiClientAxiosRefreshOptions
) {
  if (typeof refreshAuth === "function") {
    createAuthRefresh(axios, refreshAuth);
  } else {
    createAuthRefresh(axios, refreshAuth.fn, refreshAuth.options);
  }
}

export function createApiClient(
  options?: Partial<ApiClientOptions>,
  gateway?: GatewayHttpHandler
) {
  const {
    config,
    service,
    refreshAuth,
    csrftoken,
    cid,
    origin
  } = mergeApiClientOptions(options);

  config.extraHeaders = {
    ...createDefaultsExtraHeaders(csrftoken, cid, origin),
    ...config.extraHeaders
  };

  const cacheTransformResponse = toArray(config.transformResponse);
  const cacheTransformRequest = toArray(config.transformRequest);

  // 删除调用方定义的 transformRequest
  // 否则会影响 axios 默认的内置转换器
  // 调用方的定义将在最后重新被设置
  delete config.transformRequest;
  // 因为需要使用 AxiosJsonBigint 替换 axios 默认的 transformResponse
  // 所以这里设置为空数据，将移除默认的 transformer
  config.transformResponse = [];

  const client = axios.create(config);

  client.plugin
    // 内置了常规的 Json 转换，不论需不需要 bigint 支持，都需处于最前置
    .use(AxiosJsonBigint, config.parseBigint)
    .use(AxiosCaseConverter, config.caseRules)
    .use(AxiosExtraHeaders, config.extraHeaders)
    .use(AxiosNormalizeError)
    .use(AxiosProgressPercent);

  refreshAuth && useApiClientAuthRefresh(client, refreshAuth);
  axiosRetry(client, config.retry);

  gateway && useGatewayAxiosPlugin(client, gateway, service);

  client.defaults.transformResponse = toArray(
    client.defaults.transformResponse
  ).concat(cacheTransformResponse);

  client.defaults.transformRequest = toArray(
    client.defaults.transformRequest
  ).concat(cacheTransformRequest);

  return client;
}
